import { debounce } from 'lodash'
import { reaction } from 'mobx'
import { observer } from 'mobx-react-lite'
import React, { ReactNode, useContext, useEffect } from 'react'
import { Button, ButtonProps } from '../Buttons/Button'
import { DropdownOrchestrationContext, DropdownOrchestrationStore } from '../stores/DropdownOrchestrationStore'
import { useForwardRef } from 'hooks/useForwardRef'
import { SingleMenuDropdownContext, SingleMenuDropdownStore } from './SingleMenuDropdownStore'
import { getStillInsideWindowClickSignaler } from './utils'

export const SingleMenuDropdown = ({ children, store }: { children: ReactNode; store: SingleMenuDropdownStore }) => {
  const orchestrationStore = useContext(DropdownOrchestrationContext)
  useEffect(() =>
    reaction(
      () => store.isOpen,
      (isOpen, wasOpen) => isOpen && !wasOpen && orchestrationStore?.handleOpen(store.softClose),
    ),
  )

  useEffect(() => {
    if (!orchestrationStore) return
    orchestrationStore.registerClosingFn(store.softClose)
    return () => orchestrationStore?.unregisterClosingFn(store.softClose)
  }, [orchestrationStore, store])

  return <SingleMenuDropdownContext.Provider value={store}>{children}</SingleMenuDropdownContext.Provider>
}

interface SingleMenuDropdownButtonProps extends ButtonProps {
  children: React.ReactNode
  blurToleranceMs: number
}

export const SingleMenuAnchorButton = observer(
  React.forwardRef<HTMLButtonElement, SingleMenuDropdownButtonProps>(
    ({ children, blurToleranceMs, ...buttonAttributes }, forwardRef) => {
      const dropdownStore = useContext(SingleMenuDropdownContext)
      const buttonRef = useForwardRef<HTMLButtonElement>(forwardRef)

      useEffect(() => {
        dropdownStore?.setAnchorButtonRef(buttonRef)
      }, [dropdownStore, buttonRef])

      useEffect(() => {
        const inButton = getStillInsideWindowClickSignaler(buttonRef)
        dropdownStore?.registerWindowClickSignaler(inButton)
        return () => dropdownStore?.unregisterWindowClickSignaler(inButton)
      }, [dropdownStore, buttonRef])

      // COMMENT THIS TO FORCE THE DROPDOWN OPEN
      useEffect(() => {
        const handleMouseOver = dropdownStore
          ? debounce(dropdownStore.handleMouseBlur, blurToleranceMs, {
              leading: false,
              trailing: true,
            })
          : undefined
        if (handleMouseOver) window.addEventListener('mouseover', handleMouseOver)
        return () => {
          if (handleMouseOver) window.removeEventListener('mouseover', handleMouseOver)
        }
      }, [dropdownStore, blurToleranceMs])

      return (
        <Button ref={buttonRef} {...buttonAttributes}>
          {children}
        </Button>
      )
    },
  ),
)

interface SimpleMenuDropdownBodyProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode
}

export const SingleMenuDropdownBody = observer(
  React.forwardRef<HTMLDivElement, SimpleMenuDropdownBodyProps>(
    ({ children, ...attributes }: SimpleMenuDropdownBodyProps, forwardRef) => {
      const dropdownStore = useContext(SingleMenuDropdownContext)

      if (!dropdownStore?.isOpen) return <div ref={forwardRef}></div>
      return (
        <RealSingleMenuDropdownBody ref={forwardRef} {...attributes}>
          {children}
        </RealSingleMenuDropdownBody>
      )
    },
  ),
)

const RealSingleMenuDropdownBody = observer(
  React.forwardRef<HTMLDivElement, SimpleMenuDropdownBodyProps>(
    ({ children, ...divAttributes }: SimpleMenuDropdownBodyProps, forwardRef) => {
      const divRef = useForwardRef<HTMLDivElement>(forwardRef)
      const width = divRef.current?.getBoundingClientRect()?.width ?? -1
      const dropdownStore = useContext(SingleMenuDropdownContext)

      useEffect(() => {
        const inBody = getStillInsideWindowClickSignaler(divRef)
        dropdownStore?.registerWindowClickSignaler(inBody)
      }, [divRef, dropdownStore])

      useEffect(() => {
        dropdownStore?.setDropdownBodyRef(divRef)
      }, [dropdownStore, divRef, width])

      useEffect(() => {
        if (dropdownStore) window.addEventListener('click', dropdownStore?.handleMouseBlur)
        return () => {
          if (dropdownStore) window.removeEventListener('click', dropdownStore?.handleMouseBlur)
        }
      }, [dropdownStore])

      return (
        <DropdownOrchestrationContext.Provider value={new DropdownOrchestrationStore()}>
          <div ref={divRef} {...divAttributes}>
            {dropdownStore?.isOpen && children}
          </div>
        </DropdownOrchestrationContext.Provider>
      )
    },
  ),
)
